import { Alert, Group, NumberInput, Paper, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { IconRotateClockwise2, IconSearch } from "@tabler/icons-react";
import React from "react";
import { useGetMcbimFurnitureAndWallsOnFloorQuery, useGetMcbimSpacesOnFloorQuery } from "../../../../../../openapi";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import PixBimViewerV2 from "../../../../../components/PixBimViewerV2/PixBimViewerV2";
import {
  selectAllPixPolygonsForSelectedFloor,
  selectFloorplanRotation,
  selectHttpMcbimSpacesOnSelectedFloor,
  selectMcbimSpacesOnSelectedFloorArgs,
  selectRoomSearchText,
  selectSelectedFloor,
  selectSelectedFloorSpace,
} from "./floorplanSelectors";
import { setFloorplanRotationInRedux, setRoomSearchTextInRedux, setSelectedFloorSpaceIdInRedux } from "../../issueSlice";
import FloorplanSelectedSpacePanel from "./FloorplanSelectedSpacePanel";

const isPointInsidePolygon = (point: [number, number], polygon: number[][]) => {
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i]?.[0];
    const yi = polygon[i]?.[1];
    const xj = polygon[j]?.[0];
    const yj = polygon[j]?.[1];

    if (xi == null || yi == null || xj == null || yj == null) continue;

    const intersects = yi > point[1] !== yj > point[1] && point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

const FloorplanViewer: React.FC = () => {
  const dispatch = useAppDispatch();
  const selectedFloor = useAppSelector(selectSelectedFloor);
  const floorplanRotation = useAppSelector(selectFloorplanRotation);
  const roomSearchText = useAppSelector(selectRoomSearchText);
  const mcbimSpacesOnSelectedFloorArgs = useAppSelector(selectMcbimSpacesOnSelectedFloorArgs);
  const selectedFloorSpaces = useAppSelector(selectHttpMcbimSpacesOnSelectedFloor);
  const selectedSpace = useAppSelector(selectSelectedFloorSpace);
  const polygons = useAppSelector(selectAllPixPolygonsForSelectedFloor);
  const { isFetching: selectedFloorSpacesFetching, isError: selectedFloorSpacesIsError } = useGetMcbimSpacesOnFloorQuery(
    mcbimSpacesOnSelectedFloorArgs ?? { projectNo: 0, floorName: "" },
    {
      skip: mcbimSpacesOnSelectedFloorArgs == null,
    },
  );
  useGetMcbimFurnitureAndWallsOnFloorQuery(mcbimSpacesOnSelectedFloorArgs ?? { projectNo: 0, floorName: "" }, {
    skip: mcbimSpacesOnSelectedFloorArgs == null,
  });

  return (
    <Paper
      withBorder
      p="lg"
      radius="md"
      style={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        gap={4}
        style={{
          flex: 1,
          minHeight: 0,
        }}
      >
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text fw={600}>{selectedFloor || "Ingen etasje valgt"}</Text>

          <Group gap="xs" wrap="nowrap">
            <TextInput
              size="xs"
              placeholder="Søk rom"
              value={roomSearchText}
              leftSection={<IconSearch size={14} />}
              style={{ width: 180 }}
              onChange={(event) => dispatch(setRoomSearchTextInRedux(event.currentTarget.value))}
            />

            <Tooltip label="Juster vinkel" withArrow>
              <NumberInput
                value={floorplanRotation}
                step={90}
                allowDecimal={false}
                hideControls={false}
                leftSection={<IconRotateClockwise2 size={16} />}
                styles={{ input: { width: 120 } }}
                onChange={(value) => {
                  if (typeof value === "number" && Number.isFinite(value)) {
                    dispatch(setFloorplanRotationInRedux(value));
                  }
                }}
              />
            </Tooltip>
          </Group>
        </Group>

        {selectedFloor === "" && (
          <Text size="sm" c="dimmed">
            Velg en etasje for å vise rom og plantegning.
          </Text>
        )}

        {selectedFloor !== "" && selectedFloorSpacesFetching && (
          <Text size="sm" c="dimmed">
            Laster rom for {selectedFloor}...
          </Text>
        )}

        {selectedFloor !== "" && selectedFloorSpacesIsError && (
          <Alert color="red">Kunne ikke hente rom for valgt etasje.</Alert>
        )}

        {selectedFloor !== "" && !selectedFloorSpacesFetching && !selectedFloorSpacesIsError && (
          <>
            <Text size="sm" c="dimmed">
              {selectedFloorSpaces?.length ?? 0} rom lastet for {selectedFloor}.
            </Text>

            {polygons.length > 0 ? (
              <div style={{ flex: 1, minHeight: 0, height: "100%", display: "flex", gap: "1rem" }}>
                <div style={{ flex: 1, minHeight: 0, height: "100%" }}>
                  <PixBimViewerV2
                    polygons={polygons}
                    angle={floorplanRotation}
                    onClick={(id, coordinates) => {
                      if (id == null) {
                        dispatch(setSelectedFloorSpaceIdInRedux(""));
                        return;
                      }

                      const clickedSpace = selectedFloorSpaces.find((space) => space._id === id);
                      if (clickedSpace) {
                        dispatch(setSelectedFloorSpaceIdInRedux(clickedSpace._id));
                        return;
                      }

                      const spaceAtCoordinates = selectedFloorSpaces.find(
                        (space) =>
                          Array.isArray(space.polygon) && space.polygon.length > 2 && isPointInsidePolygon(coordinates, space.polygon),
                      );

                      dispatch(setSelectedFloorSpaceIdInRedux(spaceAtCoordinates?._id ?? ""));
                    }}
                  />
                </div>

                {selectedSpace && <FloorplanSelectedSpacePanel />}
              </div>
            ) : (
              <Text size="sm" c="dimmed">
                Ingen polygoner tilgjengelig for valgt etasje.
              </Text>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default FloorplanViewer;
